import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { ButtonLink } from "../../../../components/ui/button";
import {
  CTASection,
  LandingFooter,
  PricingSection,
  WhyToolKitSection,
} from "../landing";
import { FadeUp } from "../landing/FadeUp";
import { resolveTradeIcon, resolveTradeImage } from "./tradeIcons";
import { useLandingScrollToSection } from "./useLandingScrollToSection";
import type { RouteMap, TradeContent, UserDisplay } from "../../types";

type TradeLandingPageProps = {
  trade: TradeContent;
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
};

const TradeHero: React.FC<{ trade: TradeContent; routes: RouteMap }> = ({
  trade,
  routes,
}) => {
  const Icon = resolveTradeIcon(trade.icon);
  const imageUrl = resolveTradeImage(trade.imageKey);

  return (
    <section className="relative overflow-hidden bg-background pt-20 pb-24 sm:pt-28">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
            <Icon className="h-4 w-4" />
            For {trade.plural}
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {trade.heroH1}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
            {trade.heroSubhead}
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <ButtonLink
              to={routes.signup}
              size="lg"
              className="px-8 py-4 text-lg"
            >
              Start 30-day trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </ButtonLink>
            <ButtonLink
              to={routes.login}
              variant="ghost"
              size="lg"
              className="text-lg"
            >
              Log in
            </ButtonLink>
          </div>
        </div>
        <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border shadow-xl">
          <img
            src={imageUrl}
            alt={trade.cardTitle}
            className="h-full w-full object-cover"
            loading="eager"
            decoding="sync"
          />
        </div>
      </div>
    </section>
  );
};

const TradeServicesSection: React.FC<{ trade: TradeContent }> = ({ trade }) => (
  <section className="bg-card py-20">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
          {trade.plural} get a page for every service they offer
        </h2>
        <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
          ToolKit builds a search page for each service you list, so{" "}
          {trade.plural.toLowerCase()} have a page for the work they actually
          do, not just a generic homepage.
        </p>
      </FadeUp>
      <ul className="mt-10 grid gap-3 sm:grid-cols-2">
        {trade.defaultServices.map((service) => (
          <li
            key={service}
            className="flex items-start gap-3 rounded-lg border border-border bg-background p-4"
          >
            <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-premium-500" />
            <span className="text-base text-foreground">{service}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const TradeLandingPage: React.FC<TradeLandingPageProps> = ({
  trade,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
}) => {
  const scrollToSection = useLandingScrollToSection();

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="landing"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
        scrollToSection={scrollToSection}
      />
      <TradeHero trade={trade} routes={routes} />
      <TradeServicesSection trade={trade} />
      <WhyToolKitSection scrollToSection={scrollToSection} />
      <PricingSection routes={routes} />
      <CTASection routes={routes} />
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
